import type { ComponentProps } from 'react'
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, FieldError, FieldLabel } from '@speaking-coach/ui'

type NameInput = ComponentProps<typeof Input>
type ErrorMessage = { message?: string } | undefined

export function FormalNameFields({
  firstName,
  lastName,
  errors,
  prefix = 'identity',
}: {
  firstName: NameInput
  lastName: NameInput
  errors: { firstName?: ErrorMessage; lastName?: ErrorMessage }
  prefix?: string
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <FieldLabel
          htmlFor={`${prefix}-last-name`}
          label="Family name"
        />
        <Input
          {...lastName}
          id={`${prefix}-last-name`}
          autoComplete="family-name"
          required
          aria-invalid={!!errors.lastName}
        />
        <FieldError message={errors.lastName?.message ?? null} />
      </div>
      <div>
        <FieldLabel
          htmlFor={`${prefix}-first-name`}
          label="Given name"
        />
        <Input
          {...firstName}
          id={`${prefix}-first-name`}
          autoComplete="given-name"
          required
          aria-invalid={!!errors.firstName}
        />
        <FieldError message={errors.firstName?.message ?? null} />
      </div>
    </div>
  )
}

export function StudentAddressFields({
  username,
  suffix,
  onSuffixChange,
  errors,
  prefix = 'identity',
  required = true,
}: {
  username: NameInput
  suffix: 'san' | 'none'
  onSuffixChange: (value: string) => void
  errors: { username?: ErrorMessage; addressSuffix?: ErrorMessage }
  required?: boolean
  prefix?: string
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_10rem]">
      <div>
        <FieldLabel
          htmlFor={`${prefix}-username`}
          label="Preferred name"
          help={`${required ? '' : 'Optional at enrollment; the student must enter or confirm it during onboarding. '}Used in the student app and by the coach. Use kana or Latin letters. Preferences are private from the company.`}
        />
        <Input
          {...username}
          id={`${prefix}-username`}
          required={required}
          aria-invalid={!!errors.username}
        />
        <FieldError message={errors.username?.message ?? null} />
      </div>
      <div>
        <FieldLabel htmlFor={`${prefix}-suffix`} label="Coach suffix" />
        <Select value={suffix} onValueChange={onSuffixChange}>
          <SelectTrigger id={`${prefix}-suffix`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="san">san</SelectItem>
            <SelectItem value="none">No suffix</SelectItem>
          </SelectContent>
        </Select>
        <FieldError message={errors.addressSuffix?.message ?? null} />
      </div>
    </div>
  )
}
